/**
 * A marca corrente, para toda a árvore. Resolve o branding público uma vez
 * (cache de uma hora) e aplica os tokens. Se a API não responder, a Eva
 * padrão entra no lugar — a tela de login não pode depender de rede para
 * aparecer.
 *
 * ## Sem piscar a cor errada
 *
 * Enquanto a API não responde, a árvore não tem marca — e o que aparecia era
 * a Eva (rosa) até a cor certa chegar. Duas defesas, em camadas:
 *
 * 1. **Cache no aparelho.** A última marca resolvida para esta porta fica no
 *    storage e entra como `placeholderData`: quem já visitou pinta certo no
 *    primeiro frame, e a rede só confirma em segundo plano.
 * 2. **Primeira visita.** Sem cache não há cor para mostrar — `resolvida` é
 *    `false`, e a casca desenha um esqueleto neutro em vez de chutar a Eva.
 */
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { EVA_BRAND_ID, PORTA_BASE, type BrandFeature } from '@eva/schemas';
import { storage } from '../api/storage';
import { EVA_PUBLIC_BRAND, fetchPublicBrand, type PublicBrand } from './brand.api';
import type { BrandDetection } from './detect';

export interface BrandValue {
  detection: BrandDetection;
  brand: PublicBrand;
  isLoading: boolean;
  /**
   * `false` só na primeira visita a uma porta, enquanto a API ainda não
   * respondeu e não há cache: é o único momento em que a cor é desconhecida.
   */
  resolvida: boolean;
  hasFeature: (f: BrandFeature) => boolean;
}

const Ctx = createContext<BrandValue | null>(null);

const chaveDoCache = (d: BrandDetection) => `marca_${d.kind}_${d.identifier}`;

export function BrandProvider({ detection, children }: { detection: BrandDetection; children: ReactNode }) {
  const emCache = storage.getJson<PublicBrand>(chaveDoCache(detection));

  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['public-brand', detection.kind, detection.identifier],
    queryFn: () => fetchPublicBrand(detection.identifier, detection.kind),
    staleTime: 60 * 60 * 1000,
    retry: 1,
    placeholderData: emCache ?? undefined,
  });

  // Grava só o que veio da rede — o placeholder já é o cache.
  useEffect(() => {
    if (data && !isPlaceholderData) storage.setJson(chaveDoCache(detection), data);
  }, [data, isPlaceholderData, detection]);

  // Sem resposta da rede: na porta base, a marca de emergência; numa porta de
  // marca, o nome do slug — para a tela não dizer "Eva" a quem entrou pela Nest.
  const brand = data ?? (detection.identifier === PORTA_BASE || detection.identifier === EVA_BRAND_ID
    ? EVA_PUBLIC_BRAND
    : { ...EVA_PUBLIC_BRAND, name: detection.identifier });
  const features = new Set<BrandFeature>(brand.enabledFeatures ?? []);

  const value: BrandValue = {
    detection,
    brand,
    isLoading,
    resolvida: data !== undefined || !isLoading,
    hasFeature: (f) => features.has(f),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrand(): BrandValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBrand precisa de <BrandProvider>');
  return v;
}
